import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { Mail, HeartHandshake, Send } from "lucide-react";
import { FormCard } from "@/components/FormShell";
import { FindoSpinner } from "@/components/FindoSpinner";

const TYPES = [
  { key: "general", labelKey: "pub.contactTypeGeneral" },
  { key: "support", labelKey: "pub.contactTypeSupport" },
  { key: "deceased_deletion", labelKey: "pub.contactTypeDeceased" },
];

export default function Contact() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    type: params.get("type") || "general",
    name: "", email: "", subject: "", message: "",
    deceased_email: "", relationship: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isDeceased = form.type === "deceased_deletion";

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/public/contact", { method: "POST", auth: false, body: form });
      setSent(true);
      toast.success(t("pub.messageSentToast"));
    } catch (err) { toast.error(err.message || t("pub.sendImpossible")); }
    finally { setBusy(false); }
  }

  return (
    <div className="max-w-2xl mx-auto px-5 py-16" data-testid="contact-page">
      <h1 className="sr-only">{t("pub.contactUs")}</h1>

      {sent ? (
        <div className="rounded-2xl border border-teal-200 bg-teal-50 p-8 text-center" data-testid="contact-success">
          <HeartHandshake size={36} className="mx-auto text-teal-600 mb-3" />
          <p className="font-display font-bold text-slate-900 text-lg">{t("pub.thanksSent")}</p>
          <p className="text-sm text-slate-500 mt-1">{t("pub.replySoon")}</p>
        </div>
      ) : (
        <FormCard icon={Mail} title={t("pub.contactUs")} subtitle={t("pub.contactIntro")} testid="contact-form-card">
        <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
          <div>
            <Label>{t("pub.reasonLabel")}</Label>
            <select value={form.type} onChange={set("type")} className="mt-1.5 h-11 w-full rounded-full border border-border px-3 text-sm bg-white" data-testid="contact-type">
              {TYPES.map((ty) => <option key={ty.key} value={ty.key}>{t(ty.labelKey)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">{t("pub.yourName")}</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-name" />
            </div>
            <div>
              <Label htmlFor="email">{t("pub.yourEmail")}</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-email" />
            </div>
          </div>

          {isDeceased ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4 space-y-3" data-testid="contact-deceased-block">
              <p className="text-sm text-rose-800">
                {t("pub.deceasedInfo")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="demail">{t("pub.accountEmailConcerned")}</Label>
                  <Input id="demail" type="email" value={form.deceased_email} onChange={set("deceased_email")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-deceased-email" />
                </div>
                <div>
                  <Label htmlFor="rel">{t("pub.yourRelationship")}</Label>
                  <Input id="rel" value={form.relationship} onChange={set("relationship")} placeholder={t("pub.relationshipPlaceholder")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-relationship" />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="subject">{t("pub.subject")}</Label>
              <Input id="subject" value={form.subject} onChange={set("subject")} className="mt-1.5 h-11 rounded-xl" data-testid="contact-subject" />
            </div>
          )}

          <div>
            <Label htmlFor="msg">{t("pub.messageWord")}</Label>
            <Textarea id="msg" required rows={5} value={form.message} onChange={set("message")} className="mt-1.5 rounded-xl" data-testid="contact-message" />
          </div>

          <Button type="submit" disabled={busy} className="h-11 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="contact-submit">
            <Send size={16} className="mr-1.5" /> {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("pub.sending")}</span> : t("pub.sendMessage")}
          </Button>
        </form>
        </FormCard>
      )}
    </div>
  );
}
